import React, { useState } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid } from "@material-ui/core";
import md5 from 'md5';

import AppInput from "../../../../components/Input/AppInput";
import AppButton from "../../../../components/CustomButtons/AppButton";
import { reduceCreateAdminUser } from "../../../../store/slices/admin";
import SettingFields from "./SettingFields";
import { ADMIN_PERMISSIONS, SYSTEM_ROLE } from "../../../../config/AppConfig";
import { getAuthInfo } from 'store/slices/auth';
import { reduceCreateCompanyUser } from 'store/slices/companyUser';
import AppConfig from "config/AppConfig";
import { useMedia } from 'react-use';
const { PATTERNS } = AppConfig;

const hasAssignablePermission = (user, permissionKey) => {
    if (!Array.isArray(user.permissions)) {
        return true;
    }
    return ADMIN_PERMISSIONS[permissionKey] && user.permissions.includes(ADMIN_PERMISSIONS[permissionKey].id);
}

const getHiddenPermissionFields = (user) => (
    user.role === SYSTEM_ROLE.COMPANY ? ['users'] : []
)

const validationSchemaAdmin = Yup.object().shape({
    firstName: Yup.string()
        .required('validation.first_name.required').matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces')
        .max(64, "validation.max_length_64"),
    lastName: Yup.string()
        .required('validation.last_name.required').matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces')
        .max(64, "validation.max_length_64"),
    email: Yup.string().trim()
        .required('validation.email.required')
        .email('validation.email.invalid')
        .max(255, { message: 'validation.max_length', value: 255})
        .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    password: Yup.string()
        .required('validation.password.required')
        .min(8, { message: 'validation.min_length', value: 8 })
        .max(64, { message: 'validation.max_length', value: 64 })
        .matches(/[A-Z]/g, 'validation.password.uppercase')
        .matches(/[a-z]/g, 'validation.password.lowercase')
        .matches(/[0-9]/g, 'validation.password.number')
        .test('password', 'validation.password.spaces', (value) => {
            if (value && value.includes(' ')) {
                return false;
            } else {
                return true;
            }
        }),
    onboard: Yup.bool(),
    // createParticipant: Yup.bool(),
    dashboard: Yup.bool(),
    users: Yup.bool(),
    company: Yup.bool(),
    rates: Yup.bool(),
    subscriptionPlan: Yup.bool(),
    transactions: Yup.bool(),
    accountSoftware: Yup.bool(),
    paymentGateway: Yup.bool(),
    options: Yup.bool()
});

const validationSchemaCompany = Yup.object().shape({
    firstName: Yup.string()
        .required('validation.first_name.required').matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces')
        // .matches(PATTERNS.XSS_PATTERN, 'validation.xssPatterns')
        .max(64, "validation.max_length_64"),
    lastName: Yup.string()
        .required('validation.last_name.required').matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces')
        // .matches(PATTERNS.XSS_PATTERN, 'validation.xssPatterns')
        .max(64, "validation.max_length_64"),
    email: Yup.string().trim()
        .required('validation.email.required')
        .email('validation.email.invalid')
        .max(255, { message: 'validation.max_length', value: 255})
        .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    password: Yup.string()
        .required('validation.password.required')
        .min(8, { message: 'validation.min_length', value: 8 })
        .max(64, { message: 'validation.max_length', value: 64 })
        .matches(/[A-Z]/g, 'validation.password.uppercase')
        .matches(/[a-z]/g, 'validation.password.lowercase')
        .matches(/[0-9]/g, 'validation.password.number')
        .test('password', 'validation.password.spaces', (value) => {
            if (value && value.includes(' ')) {
                return false;
            } else {
                return true;
            }
        }),
    isEmailOnboard: Yup.bool(),
    isEmailCreatedParticipant: Yup.bool(),
    dashboard: Yup.bool(),
    users: Yup.bool(),
    jobs: Yup.bool(),
    workers: Yup.bool(),
    clients: Yup.bool(),
    booking: Yup.bool(),
    billing: Yup.bool(),
    payroll: Yup.bool()
})

const AdminUserAddForm = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const user = useSelector(getAuthInfo);
    const below768 = useMedia('(max-width: 950px)');
    const [submitting, setSubmitting] = useState(false);

    const _submit = (data) => {
        setSubmitting(true);
        const permissions = [];
        const hiddenPermissionFields = getHiddenPermissionFields(user);
        Object.keys(data).forEach((key) => {
            if (
                typeof data[key] === 'boolean' &&
                data[key] === true &&
                ADMIN_PERMISSIONS[key] &&
                hasAssignablePermission(user, key) &&
                !hiddenPermissionFields.includes(key)
            ) {
                permissions.push(ADMIN_PERMISSIONS[key].id);
            }
        })
        const request = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email.trim(),
            passwordHash: md5(data.password),
            permissions,
            // emailCreatedParticipant: data.isEmailCreatedParticipant,
            emailOnboard: data.isEmailOnboard
        }
        if(user.role === SYSTEM_ROLE.COMPANY) {
            request.company_id = user.companyId;
            request.isEmailCreatedParticipant = data.isEmailCreatedParticipant;
            request.isEmailOnboard = data.isEmailOnboard;
            delete request.emailOnboard;
            delete request.emailCreatedParticipant;

        }
        delete request.password;
        dispatch(user.role === SYSTEM_ROLE.COMPANY ? reduceCreateCompanyUser(request) : reduceCreateAdminUser(request))
            .then((result) => {
                if(result.payload !== undefined) {
                    history.goBack();
                }
                setSubmitting(false);
            })
    }

    return (
        <Formik
            initialValues={{
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                isEmailOnboard: true,
                isEmailCreatedParticipant: user.role === SYSTEM_ROLE.COMPANY ? true : false,
                dashboard: hasAssignablePermission(user, 'dashboard'),
                users: user.role !== SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'users'),
                company: (user.role === SYSTEM_ROLE.SUPER_ADMIN || user.role === SYSTEM_ROLE.MANAGER) && hasAssignablePermission(user, 'company'),
                subscriptionPlan: (user.role === SYSTEM_ROLE.SUPER_ADMIN || user.role === SYSTEM_ROLE.MANAGER) && hasAssignablePermission(user, 'subscriptionPlan'),
                transactions: (user.role === SYSTEM_ROLE.SUPER_ADMIN || user.role === SYSTEM_ROLE.MANAGER) && hasAssignablePermission(user, 'transactions'),
                accountSoftware: (user.role === SYSTEM_ROLE.SUPER_ADMIN || user.role === SYSTEM_ROLE.MANAGER) && hasAssignablePermission(user, 'accountSoftware'),
                paymentGateway: (user.role === SYSTEM_ROLE.SUPER_ADMIN || user.role === SYSTEM_ROLE.MANAGER) && hasAssignablePermission(user, 'paymentGateway'),
                options: user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'options'),
                jobs:  user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'jobs'),
                workers:  user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'workers'),
                clients:  user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'clients'),
                rates: user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'rates'),
                booking:  user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'booking'),
                billing:  user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'billing'),
                payroll:  user.role === SYSTEM_ROLE.COMPANY && hasAssignablePermission(user, 'payroll')
            }}
            validationSchema={user.role === SYSTEM_ROLE.COMPANY ? validationSchemaCompany : validationSchemaAdmin}
            onSubmit={_submit}
            enableReinitialize
        >
            {({ handleSubmit }) => (
                <>
                    <Grid container spacing={3}>
                        <Grid item sm={12} md={below768 ? 12 : 6} style={{ width: below768 ? '100%' : 'auto'}}>
                            <Field
                                component={AppInput}
                                name="email"
                                label={t('label.email')}
                                required
                            />
                            <Field
                                component={AppInput}
                                name="firstName"
                                label={t('label.first_name')}
                                required
                            />
                            <Field
                                component={AppInput}
                                name="lastName"
                                label={t('label.last_name')}
                                required
                            />
                            <Field
                                component={AppInput}
                                name="password"
                                label={t('label.password')}
                                type="password"
                                required
                            />
                        </Grid>
                        <Grid item sm={12} md={6}>
                            <SettingFields
                                role={user.role}
                                permissions={user.permissions}
                                hiddenPermissionFields={getHiddenPermissionFields(user)}
                            />
                        </Grid>
                    </Grid>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3}>
                        <AppButton
                            label={t('button.back')}
                            onClick={() => history.goBack()}
                            mode="outlined"
                            style={{
                                marginRight: '0.5rem'
                            }}
                        />
                        <AppButton
                            label={t('button.save')}
                            onClick={handleSubmit}
                            isLoading={submitting}
                            disabled={submitting}
                        />
                    </Box>
                </>
            )}
        </Formik>
    )
}

export default AdminUserAddForm;
